/**
 * Reminders Data Structure and Management
 * Handles all reminder logic for music release workflow
 */

// Master reminder templates based on user requirements
export const REMINDER_TEMPLATES = {
  // Distributor Upload Deadlines
  DISTRIBUTOR_UPLOAD: {
    id: 'distributor_upload',
    title: 'Distributor Upload Deadlines',
    description: 'Make sure songs are delivered in time to DSPs and for editorial pitching (2–3 weeks out). For Spotify, make sure to use the pitch tool in your Spotify for Artists account to pitch your songs directly for playlist consideration. They want to hear about things like what you plan to do to market the song, if you are playing live and how and/or why it came together. To read more on this topic, check out the Spotify Pitch Tool Article in the Dizee knowledge base.',
    actions: ['Completed', 'Remind Me Later']
  },

  // 5 weeks out workflow
  FIVE_WEEKS_OUT: {
    id: 'five_weeks_out',
    title: 'Publisher & PRO Notification',
    description: 'Notify your publisher and PRO about the upcoming release. Deliver splits and label copy.',
    actions: ['Completed', 'Remind Me Later']
  },

  // 4 weeks out workflow
  FOUR_WEEKS_OUT: {
    id: 'four_weeks_out', 
    title: 'DSP Submission',
    description: 'Ensure the upcoming release is submitted to your distributor or label, including master, artwork, and label copy assets.',
    actions: ['Completed', 'Remind Me Later']
  },

  // 3 weeks out workflow
  THREE_WEEKS_OUT: {
    id: 'three_weeks_out',
    title: 'Spotify for Artists Confirmation',
    description: 'Verify that the song appears under the Upcoming Releases tab in Spotify for Artists.',
    actions: ['Completed', 'Remind Me Later']
  },

  // 2 weeks out workflow
  TWO_WEEKS_OUT: {
    id: 'two_weeks_out',
    title: 'Final Pitch Deadline',
    description: 'Complete any DSP pitches before the final cutoff (2 weeks before release) to maximize support and playlist opportunities.',
    actions: ['Completed', 'Remind Me Later']
  },

  // 48 hours out workflow
  FORTY_EIGHT_HOURS_OUT: {
    id: 'forty_eight_hours_out',
    title: 'Canvas & Visualizer Check',
    description: 'Confirm the Spotify Canvas and YouTube Visualizer are uploaded and ready for release.',
    actions: ['Completed', 'Remind Me Later']
  },

  // Release day - 10am PST Thursday
  RELEASE_DAY: {
    id: 'release_day',
    title: 'Song Release Announcement',
    description: 'Celebrate the release! Announce across socials and check the Playlisting App to see playlist placements.',
    actions: ['Viewed']
  },

  // 7 days after release
  SEVEN_DAYS_POST: {
    id: 'seven_days_post',
    title: 'Release Performance Recap',
    description: 'Review post-release performance including streams, playlists, and chart metrics.',
    actions: ['Viewed']
  },

  // 14 days after release
  FOURTEEN_DAYS_POST: {
    id: 'fourteen_days_post',
    title: 'PRO & Publisher Registration',
    description: 'Confirm that the song and writer splits are registered with the correct PRO and publisher.',
    actions: ['Completed', 'Remind Me Later']
  },

  // Custom reminder template
  CUSTOM: {
    id: 'custom',
    title: 'Custom Reminder',
    description: 'User-created custom reminder',
    actions: ['Completed', 'Remind Me Later']
  },

  // ========== BOOKING REMINDERS ==========
  
  // 1. Offer Intake - Log Offer
  BOOKING_OFFER_LOG: {
    id: 'booking_offer_log',
    title: 'Offer Intake - Log Offer (Manually via Email)',
    description: 'Enter offer details, set status Pending',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'immediate',
    notify: ['Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 2. Offer Intake - Calendar Hold
  BOOKING_CALENDAR_HOLD: {
    id: 'booking_calendar_hold',
    title: 'Offer Intake - Calendar Hold (via OpenAI integration)',
    description: 'Place tentative hold on artist calendar',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'immediate',
    notify: ['Manager', 'Day-to-Day', 'Assistant', 'Artist'],
    type: 'booking'
  },

  // 3. Offer Decision - Confirm/Decline
  BOOKING_OFFER_DECISION: {
    id: 'booking_offer_decision',
    title: 'Offer Decision - Confirm/Decline',
    description: 'Decide: if pass, relieve hold & file as Declined',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'weekly_until_decision',
    reminderBeforeExpiration: true,
    notify: ['Manager', 'Day-to-Day', 'Assistant', 'Artist'],
    type: 'booking'
  },

  // 4. Offer Decision - Confirmed
  BOOKING_CONFIRMED: {
    id: 'booking_confirmed',
    title: 'Offer Decision - Confirmed',
    description: 'Set status Confirmed: trigger downstream reminders',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'immediate',
    notify: ['Manager', 'Day-to-Day', 'Assistant', 'Artist', 'Tour Manager'],
    type: 'booking'
  },

  // 5. Contracting
  BOOKING_CONTRACTING: {
    id: 'booking_contracting',
    title: 'Contracting',
    description: 'Execute contract, upload to system',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '7_days_post_confirm',
    recurringWeekly: true,
    notify: ['Manager', 'Day to Day', 'Assistant'],
    type: 'booking'
  },

  // 6. Marketing Launch
  BOOKING_MARKETING_LAUNCH: {
    id: 'booking_marketing_launch',
    title: 'Marketing Launch',
    description: 'Send marketing pack: assets, promo plan, assets, and plan',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '7_days_post_contract',
    recurringWeekly: true,
    notify: ['Manager', 'Day to Day', 'Assistant'],
    type: 'booking'
  },

  // 7. Show Advance
  BOOKING_SHOW_ADVANCE: {
    id: 'booking_show_advance',
    title: 'Show Advance',
    description: 'Advance email: tech & hospitality riders, travel needs',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '30_days_out',
    recurringWeekly: true,
    notify: ['Manager', 'Tour Manager', 'Production Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 8. Ticket Counts
  BOOKING_TICKET_COUNTS: {
    id: 'booking_ticket_counts',
    title: 'Ticket Counts',
    description: 'Monitor ticket counts: escalate calendars close to show',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'weekly_from_8wks',
    recurringPattern: 'escalating',
    notify: ['Manager', 'Tour Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 9. Travel Finalization
  BOOKING_TRAVEL_FINALIZATION: {
    id: 'booking_travel_finalization',
    title: 'Travel Finalization',
    description: 'Book flights, hotels, ground transport',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '4_weeks_pre_show',
    recurringWeekly: true,
    notify: ['Manager', 'Tour Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 10. Travel Confirmation
  BOOKING_TRAVEL_CONFIRMATION: {
    id: 'booking_travel_confirmation',
    title: 'Travel Confirmation',
    description: 'Confirmation reminder showing booked travel plans and daily schedule',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: 'immediate_after_travel_booked',
    notify: ['Manager', 'Tour Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 11. Guest List & M&Q
  BOOKING_GUEST_LIST: {
    id: 'booking_guest_list',
    title: 'Guest List & M&Q',
    description: 'Finalize guest list and meet & greet plan',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '1_week_pre_show',
    reminderPattern: ['1_week', '48_hours', '24_hours'],
    notify: ['Manager', 'Tour Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  },

  // 12. Post-Show Settlement
  BOOKING_POST_SHOW_SETTLEMENT: {
    id: 'booking_post_show_settlement',
    title: 'Post-Show Settlement',
    description: 'Confirm settlement and log final ticket countersales',
    actions: ['Completed', 'Remind Me Later'],
    reminderTiming: '48_hours_post_show',
    recurringWeekly: true,
    notify: ['Manager', 'Tour Manager', 'Day-to-Day', 'Assistant'],
    type: 'booking'
  }
};

// Reminder instance structure
export class ReminderInstance {
  constructor(templateId, releaseId, releaseName, releaseDate, metadata = {}) {
    this.id = `${templateId}_${releaseId}_${Date.now()}`;
    this.templateId = templateId;
    this.releaseId = releaseId;
    this.releaseName = releaseName;
    this.releaseDate = releaseDate;
    this.template = REMINDER_TEMPLATES[templateId.toUpperCase()];
    this.status = 'pending'; // pending, completed, snoozed, dismissed
    this.createdAt = new Date().toISOString();
    this.metadata = metadata; // SET METADATA BEFORE CALCULATING DUE DATE
    this.dueDate = this.calculateDueDate();
    this.snoozeUntil = null;
    this.completedAt = null;
    this.viewed = false;
  }

  calculateDueDate(newReleaseDate = null) {
    const releaseDate = new Date(newReleaseDate || this.releaseDate);
    const templateId = this.templateId.toLowerCase();
    
    // Music release reminders
    switch (templateId) {
      case 'five_weeks_out':
        return new Date(releaseDate.getTime() - (35 * 24 * 60 * 60 * 1000));
      case 'four_weeks_out':
        return new Date(releaseDate.getTime() - (28 * 24 * 60 * 60 * 1000));
      case 'three_weeks_out':
        return new Date(releaseDate.getTime() - (21 * 24 * 60 * 60 * 1000));
      case 'two_weeks_out':
        return new Date(releaseDate.getTime() - (14 * 24 * 60 * 60 * 1000));
      case 'forty_eight_hours_out':
        return new Date(releaseDate.getTime() - (2 * 24 * 60 * 60 * 1000));
      case 'release_day':
        // Set to 10am PST on release day
        const releaseDayPST = new Date(releaseDate);
        releaseDayPST.setHours(10, 0, 0, 0); // 10am PST
        return releaseDayPST;
      case 'seven_days_post':
        return new Date(releaseDate.getTime() + (7 * 24 * 60 * 60 * 1000));
      case 'fourteen_days_post':
        return new Date(releaseDate.getTime() + (14 * 24 * 60 * 60 * 1000));
      
      // Booking reminders (use perf_date or relevant date from metadata)
      case 'booking_offer_log':
      case 'booking_calendar_hold':
        // Immediate - set to now
        return new Date();
      
      case 'booking_offer_decision':
        // Weekly until decision, first reminder 1 week after offer entry
        const offerEntryDate = this.metadata?.offerEntryDate ? new Date(this.metadata.offerEntryDate) : new Date();
        return new Date(offerEntryDate.getTime() + (7 * 24 * 60 * 60 * 1000));
      
      case 'booking_confirmed':
        // Immediate upon confirmation
        return new Date();
      
      case 'booking_contracting':
        // 7 days post-confirm, recurring weekly
        const confirmDate = this.metadata?.confirmDate ? new Date(this.metadata.confirmDate) : new Date();
        return new Date(confirmDate.getTime() + (7 * 24 * 60 * 60 * 1000));
      
      case 'booking_marketing_launch':
        // 7 days post-contract, recurring weekly
        const contractDate = this.metadata?.contractDate ? new Date(this.metadata.contractDate) : releaseDate;
        return new Date(contractDate.getTime() + (7 * 24 * 60 * 60 * 1000));
      
      case 'booking_show_advance':
        // 30 days out from show date, recurring weekly
        return new Date(releaseDate.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      case 'booking_ticket_counts':
        // Weekly from 8 weeks out (56 days)
        return new Date(releaseDate.getTime() - (56 * 24 * 60 * 60 * 1000));
      
      case 'booking_travel_finalization':
        // 4 weeks pre-show (28 days), recurring weekly
        return new Date(releaseDate.getTime() - (28 * 24 * 60 * 60 * 1000));
      
      case 'booking_travel_confirmation':
        // Immediate after travel booked
        return new Date();
      
      case 'booking_guest_list':
        // 1 week pre-show
        return new Date(releaseDate.getTime() - (7 * 24 * 60 * 60 * 1000));
      
      case 'booking_post_show_settlement':
        // 48 hours post-show
        return new Date(releaseDate.getTime() + (2 * 24 * 60 * 60 * 1000));
      
      default:
        return new Date(releaseDate);
    }
  }

  isOverdue() {
    try {
      if (!this.dueDate) return false;
      const dueDate = new Date(this.dueDate);
      const now = new Date();
      
      // Check if dueDate is valid
      if (isNaN(dueDate.getTime())) return false;
      
      const isOverdue = now > dueDate && (this.status === 'pending' || this.status === 'overdue');
      
      // Debug logging when enabled
      if (window.debugOverdueCheck) {
        console.log(`🔍 isOverdue() check for "${this.template?.title}":`, {
          dueDate: this.dueDate,
          dueDateParsed: dueDate.toISOString(),
          now: now.toISOString(),
          status: this.status,
          nowGreaterThanDue: now > dueDate,
          statusIsPending: this.status === 'pending',
          statusIsOverdue: this.status === 'overdue',
          statusIsValidForOverdue: this.status === 'pending' || this.status === 'overdue',
          result: isOverdue
        });
      }
      
      return isOverdue;
    } catch (error) {
      console.error('Error checking if reminder is overdue:', error, this);
      return false;
    }
  }

  isDueToday() {
    try {
      if (!this.dueDate) return false;
      const today = new Date();
      const due = new Date(this.dueDate);
      
      // Check if dueDate is valid
      if (isNaN(due.getTime())) return false;
      
      return today.toDateString() === due.toDateString() && this.status === 'pending';
    } catch (error) {
      console.error('Error checking if reminder is due today:', error, this);
      return false;
    }
  }

  getDaysUntilDue() {
    try {
      if (!this.dueDate) return 0;
      const now = new Date();
      const due = new Date(this.dueDate);
      
      // Check if dueDate is valid
      if (isNaN(due.getTime())) return 0;
      
      const diffTime = due - now;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      console.error('Error calculating days until due:', error, this);
      return 0;
    }
  }

  complete() {
    this.status = 'completed';
    this.completedAt = new Date().toISOString();
  }

  revert() {
    this.status = 'pending';
    this.completedAt = null;
  }

  snooze(hours = 24) {
    this.status = 'snoozed';
    this.snoozeUntil = new Date(Date.now() + (hours * 60 * 60 * 1000)).toISOString();
  }

  dismiss() {
    this.status = 'dismissed';
  }

  markViewed() {
    this.viewed = true;
  }
}

// Reminders Manager Class
export class RemindersManager {
  constructor() {
    this.reminders = this.loadReminders();
    this.setupEventListeners();
  }

  // Method to force reload reminders from localStorage
  reloadReminders() {
    this.reminders = this.loadReminders();
    return this.reminders;
  }

  // Load reminders from backend (with localStorage fallback)
  loadReminders() {
    // Try loading from backend first
    this.loadFromBackend();
    
    // Return localStorage data immediately while backend loads
    try {
      const stored = localStorage.getItem('dizee_reminders');
      if (stored) {
        const parsed = JSON.parse(stored);
        
        // Convert plain objects back to ReminderInstance objects
        const restored = parsed.map((data, index) => {
          // Check for missing required data
          if (!data.templateId || !data.releaseId || !data.releaseName || !data.releaseDate) {
            console.warn(`⚠️ Reminder ${index + 1} missing required fields:`, {
              id: data.id,
              hasTemplateId: !!data.templateId,
              hasReleaseId: !!data.releaseId,
              hasReleaseName: !!data.releaseName,
              hasReleaseDate: !!data.releaseDate
            });
          }
          
          const reminder = new ReminderInstance(data.templateId, data.releaseId, data.releaseName, data.releaseDate, data.metadata || {});
          
          // Carefully restore properties without overwriting methods
          reminder.id = data.id;
          reminder.status = data.status;
          reminder.viewed = data.viewed;
          reminder.completedAt = data.completedAt;
          reminder.snoozeUntil = data.snoozeUntil;
          reminder.createdAt = data.createdAt;
          reminder.updatedAt = data.updatedAt;
          reminder.dueDate = data.dueDate; // Important: make sure dueDate is restored
          
          // Verify critical methods exist
          if (typeof reminder.isOverdue !== 'function') {
            console.error(`❌ Reminder ${reminder.id} missing isOverdue method!`);
          }
          
          return reminder;
        });
        
        console.log(`📦 Loaded ${restored.length} reminders from storage`);
        return restored;
      }
      return [];
    } catch (error) {
      console.error('Error loading reminders:', error);
      return [];
    }
  }

  // Load reminders from backend asynchronously
  async loadFromBackend() {
    try {
      const { remindersApi } = await import('./remindersApi');
      const result = await remindersApi.getUserReminders();
      
      if (result.success && result.reminders.length > 0) {
        console.log(`☁️  Loaded ${result.reminders.length} reminders from backend`);
        
        // Convert backend data to ReminderInstance objects
        const backendReminders = result.reminders.map(data => {
          const reminder = new ReminderInstance(
            data.templateId, 
            data.releaseId, 
            data.releaseName, 
            data.releaseDate, 
            data.metadata || {}
          );
          
          // Restore all properties
          Object.assign(reminder, {
            id: data.id,
            status: data.status,
            viewed: data.viewed,
            completedAt: data.completedAt,
            snoozeUntil: data.snoozeUntil,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            dueDate: data.dueDate
          });
          
          return reminder;
        });
        
        // Update in-memory reminders
        this.reminders = backendReminders;
        
        // Update localStorage as cache
        localStorage.setItem('dizee_reminders', JSON.stringify(this.reminders));
        
        // Notify components
        window.dispatchEvent(new CustomEvent('remindersLoaded', {
          detail: { source: 'backend', count: backendReminders.length }
        }));
      } else {
        console.log('📱 No reminders from backend, using localStorage');
      }
    } catch (error) {
      console.warn('⚠️  Failed to load from backend, using localStorage:', error.message);
    }
  }

  // Save reminders to both localStorage and backend
  saveReminders() {
    try {
      // Save to localStorage immediately (synchronous)
      localStorage.setItem('dizee_reminders', JSON.stringify(this.reminders));
      
      // Dispatch custom event to notify components in same tab
      window.dispatchEvent(new CustomEvent('localStorageUpdate', {
        detail: {
          key: 'dizee_reminders',
          newValue: JSON.stringify(this.reminders)
        }
      }));
      
      console.log('💾 Reminders saved to localStorage');
      
      // Sync to backend asynchronously (non-blocking)
      this.syncToBackend();
    } catch (error) {
      console.error('Error saving reminders:', error);
    }
  }

  // Sync reminders to backend asynchronously
  async syncToBackend() {
    try {
      const { remindersApi } = await import('./remindersApi');
      const result = await remindersApi.bulkSaveReminders(this.reminders);
      
      if (result.success) {
        console.log(`☁️  Synced ${result.saved || this.reminders.length} reminders to backend`);
      } else {
        console.warn('⚠️  Backend sync failed:', result.error);
      }
    } catch (error) {
      console.warn('⚠️  Backend sync error:', error.message);
      // Don't throw - we already saved to localStorage
    }
  }

  // Create reminders for a new release
  createRemindersForRelease(releaseId, releaseName, releaseDate, metadata = {}) {
    const templateIds = [
      'FIVE_WEEKS_OUT',
      'FOUR_WEEKS_OUT', 
      'THREE_WEEKS_OUT',
      'TWO_WEEKS_OUT',
      'FORTY_EIGHT_HOURS_OUT',
      'RELEASE_DAY',
      'SEVEN_DAYS_POST',
      'FOURTEEN_DAYS_POST'
    ];

    const newReminders = templateIds.map(templateId => 
      new ReminderInstance(templateId, releaseId, releaseName, releaseDate, metadata)
    );

    this.reminders.push(...newReminders);
    this.saveReminders();
    
    return newReminders;
  }

  // Create reminders for a new booking
  createRemindersForBooking(bookingId, bookingData, metadata = {}) {
    const perfDate = bookingData.perf_date || bookingData.performance_date;
    const status = bookingData.status || 'pending';
    const venue = bookingData.venue || 'Unknown Venue';
    const city = bookingData.city || '';
    const bookingName = `${venue}${city ? ` - ${city}` : ''}`;
    
    console.log(`🎫 Creating booking reminders for: ${bookingName} (${perfDate})`);
    
    if (!perfDate) {
      console.warn('⚠️ No performance date provided, skipping booking reminders');
      return [];
    }

    // Determine which reminders to create based on booking status
    let templateIds = [];
    
    if (status === 'pending') {
      // Pending offers get offer-related reminders
      templateIds = [
        'BOOKING_OFFER_LOG',
        'BOOKING_CALENDAR_HOLD',
        'BOOKING_OFFER_DECISION'
      ];
    } else if (status === 'confirmed' || status === 'announced' || status === 'private') {
      // Confirmed bookings get full workflow reminders
      templateIds = [
        'BOOKING_CONFIRMED',
        'BOOKING_CONTRACTING',
        'BOOKING_MARKETING_LAUNCH',
        'BOOKING_SHOW_ADVANCE',
        'BOOKING_TICKET_COUNTS',
        'BOOKING_TRAVEL_FINALIZATION',
        'BOOKING_GUEST_LIST',
        'BOOKING_POST_SHOW_SETTLEMENT'
      ];
    } else if (status === 'canceled') {
      // No reminders for canceled bookings
      console.log('🚫 Booking is canceled, no reminders created');
      return [];
    }

    // Create the reminders with booking metadata
    const bookingMetadata = {
      ...metadata,
      bookingId: bookingId,
      venue: venue,
      city: city,
      state: bookingData.state,
      status: status,
      artist: bookingData.artist,
      guarantee: bookingData.guarantee,
      promoter: bookingData.promoter,
      offerEntryDate: bookingData.created_at || new Date().toISOString(),
      confirmDate: status === 'confirmed' ? new Date().toISOString() : null,
      type: 'booking'
    };

    const newReminders = templateIds.map(templateId => 
      new ReminderInstance(templateId, bookingId, bookingName, perfDate, bookingMetadata)
    );

    // Filter out reminders that are already in the past
    const now = new Date();
    const validReminders = newReminders.filter(reminder => {
      const dueDate = new Date(reminder.dueDate);
      return dueDate >= now || reminder.templateId.includes('post_show');
    });

    if (validReminders.length < newReminders.length) {
      console.log(`⏭️ Skipped ${newReminders.length - validReminders.length} past reminders`);
    }

    this.reminders.push(...validReminders);
    this.saveReminders();
    
    console.log(`✅ Created ${validReminders.length} booking reminders`);
    return validReminders;
  }

  // Update booking reminders when booking data changes
  updateRemindersForBooking(bookingId, updatedBooking) {
    const existingReminders = this.reminders.filter(r => 
      r.releaseId === bookingId && r.metadata?.type === 'booking'
    );
    
    if (existingReminders.length === 0) {
      console.log('📝 No existing reminders found for booking, creating new ones...');
      return this.createRemindersForBooking(bookingId, updatedBooking);
    }

    const newPerfDate = updatedBooking.perf_date || updatedBooking.performance_date;
    const newStatus = updatedBooking.status;
    
    console.log(`🔄 Updating ${existingReminders.length} booking reminders...`);

    // If status changed to canceled, remove all pending reminders
    if (newStatus === 'canceled') {
      console.log('🚫 Booking canceled, removing all pending reminders');
      this.reminders = this.reminders.filter(r => 
        !(r.releaseId === bookingId && r.metadata?.type === 'booking' && r.status === 'pending')
      );
      this.saveReminders();
      return { deleted: existingReminders.length };
    }

    // If status changed from pending to confirmed, create new workflow reminders
    const oldStatus = existingReminders[0]?.metadata?.status;
    if (oldStatus === 'pending' && newStatus === 'confirmed') {
      console.log('✅ Status changed to confirmed, creating workflow reminders');
      // Remove old pending reminders
      this.reminders = this.reminders.filter(r => r.releaseId !== bookingId);
      this.saveReminders();
      // Create new confirmed workflow reminders
      return this.createRemindersForBooking(bookingId, updatedBooking, { statusChanged: true });
    }

    // Update existing reminders with new date/data
    let updatedCount = 0;
    existingReminders.forEach(reminder => {
      if (reminder.status === 'completed') {
        return; // Don't update completed reminders
      }

      // Update performance date if it changed
      if (newPerfDate && reminder.releaseDate !== newPerfDate) {
        reminder.releaseDate = newPerfDate;
        reminder.dueDate = reminder.calculateDueDate(newPerfDate);
        updatedCount++;
      }

      // Update metadata
      reminder.metadata = {
        ...reminder.metadata,
        venue: updatedBooking.venue,
        city: updatedBooking.city,
        state: updatedBooking.state,
        status: newStatus
      };
    });

    this.saveReminders();
    console.log(`✅ Updated ${updatedCount} booking reminders`);
    
    return { updated: updatedCount };
  }

  // Delete all reminders for a specific booking
  deleteRemindersForBooking(bookingId) {
    const remindersToDelete = this.reminders.filter(r => 
      r.releaseId === bookingId && r.metadata?.type === 'booking'
    );
    
    if (remindersToDelete.length === 0) {
      return 0;
    }

    this.reminders = this.reminders.filter(r => 
      !(r.releaseId === bookingId && r.metadata?.type === 'booking')
    );
    
    this.saveReminders();
    console.log(`🗑️ Deleted ${remindersToDelete.length} booking reminders`);
    
    return remindersToDelete.length;
  }

  // Update existing reminders when release date changes
  updateRemindersForRelease(releaseId, updatedRelease) {
    const existingReminders = this.reminders.filter(r => r.releaseId === releaseId);
    
    if (existingReminders.length === 0) {
      return; // No reminders to update
    }

    const oldReleaseDate = existingReminders[0].releaseDate;
    const newReleaseDate = updatedRelease.release_date;
    
    // Check if the release date has actually changed
    if (oldReleaseDate === newReleaseDate) {
      return; // No change needed
    }

    console.log(`📅 Updating reminders for "${existingReminders[0].releaseName}"`);
    console.log(`   Old date: ${oldReleaseDate}`);
    console.log(`   New date: ${newReleaseDate}`);

    let updatedCount = 0;
    let newOverdueCount = 0;

    // Update each reminder with the new release date
    existingReminders.forEach(reminder => {
      if (reminder.status === 'completed') {
        // Don't update completed reminders
        return;
      }

      const oldDueDate = reminder.dueDate;
      
      // Recalculate the due date based on the new release date
      reminder.releaseDate = newReleaseDate;
      reminder.dueDate = reminder.calculateDueDate(newReleaseDate);
      
      // Check if this reminder is now overdue
      const wasOverdue = new Date(oldDueDate) < new Date();
      const isNowOverdue = reminder.isOverdue();
      
      if (!wasOverdue && isNowOverdue) {
        newOverdueCount++;
        console.log(`   ⚠️ "${reminder.template.title}" is now OVERDUE (due: ${reminder.dueDate})`);
        
        // Reset status and viewed flag so overdue reminders can be shown
        if (reminder.status === 'snoozed') {
          reminder.status = 'pending';
        }
        // Always reset viewed flag for newly overdue reminders
        reminder.viewed = false;
      } else if (wasOverdue && !isNowOverdue) {
        console.log(`   ✅ "${reminder.template.title}" is no longer overdue (due: ${reminder.dueDate})`);
      } else {
        console.log(`   📝 "${reminder.template.title}" updated (due: ${reminder.dueDate})`);
      }
      
      updatedCount++;
    });

    // Save the updated reminders
    this.saveReminders();
    
    console.log(`✅ Updated ${updatedCount} reminders`);
    
    if (newOverdueCount > 0) {
      console.log(`⚠️ ${newOverdueCount} reminders are now OVERDUE and will appear in notifications`);
      
      // Trigger a notification check after a short delay
      setTimeout(() => {
        // Force a reminder notification check
        window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
      }, 1000);
    }

    return {
      updated: updatedCount,
      newOverdue: newOverdueCount
    };
  }

  // Delete all reminders for a specific release
  deleteRemindersForRelease(releaseId) {
    const remindersToDelete = this.reminders.filter(r => r.releaseId === releaseId);
    
    if (remindersToDelete.length === 0) {
      console.log(`📝 No reminders found for release ID: ${releaseId}`);
      return [];
    }
    
    // Remove reminders from the array
    this.reminders = this.reminders.filter(r => r.releaseId !== releaseId);
    
    // Save updated reminders
    this.saveReminders();
    
    console.log(`🗑️ Deleted ${remindersToDelete.length} reminders for release: ${releaseId}`);
    
    return remindersToDelete;
  }

  // Get all reminders
  getAllReminders() {
    return this.reminders;
  }

  // Get active reminders (pending and not snoozed)
  getActiveReminders() {
    const now = new Date();
    return this.reminders.filter(reminder => {
      if (reminder.status === 'completed' || reminder.status === 'dismissed') {
        return false;
      }
      
      if (reminder.status === 'snoozed' && reminder.snoozeUntil) {
        return new Date(reminder.snoozeUntil) <= now;
      }
      
      return reminder.status === 'pending';
    });
  }

  // Get overdue reminders
  getOverdueReminders() {
    return this.getActiveReminders().filter(reminder => reminder.isOverdue());
  }

  // Get reminders due today
  getTodaysReminders() {
    return this.getActiveReminders().filter(reminder => reminder.isDueToday());
  }

  // Get upcoming reminders (next 7 days)
  getUpcomingReminders(days = 7) {
    const now = new Date();
    const future = new Date(now.getTime() + (days * 24 * 60 * 60 * 1000));
    
    return this.getActiveReminders().filter(reminder => {
      const dueDate = new Date(reminder.dueDate);
      return dueDate > now && dueDate <= future;
    });
  }

  // Get unviewed reminders count
  getUnviewedCount() {
    const active = this.getActiveReminders();
    const unviewed = active.filter(reminder => !reminder.viewed);
    return unviewed.length;
  }

  // Get critical reminders (overdue + urgent)
  getCriticalReminders() {
    return this.getActiveReminders().filter(reminder => 
      reminder.isOverdue() || reminder.template?.priority === 'critical'
    );
  }

  // Get completed reminders
  getCompletedReminders() {
    return this.reminders.filter(reminder => reminder.status === 'completed');
  }

  // Clean up problematic reminders (ones with "auto" or empty names)
  cleanupProblematicReminders() {
    const originalCount = this.reminders.length;
    const toRemove = [];
    
    this.reminders = this.reminders.filter(reminder => {
      const hasValidName = reminder.releaseName && 
                          reminder.releaseName !== 'auto' && 
                          reminder.releaseName.trim() !== '';
      
      if (!hasValidName) {
        toRemove.push({
          id: reminder.id,
          releaseName: reminder.releaseName,
          title: reminder.title,
          metadata: reminder.metadata
        });
      }
      
      return hasValidName;
    });
    
    const removedCount = originalCount - this.reminders.length;
    if (removedCount > 0) {
      console.log(`🧹 Cleaned up ${removedCount} problematic reminders:`, toRemove);
      this.saveReminders();
    }
    
    return removedCount;
  }

  // Helper to parse time strings like "8am", "12pm"
  parseTimeToHours(timeString) {
    const match = timeString.match(/^(\d+)(am|pm)$/i);
    if (!match) return 8; // Default to 8am
    
    let hours = parseInt(match[1]);
    const period = match[2].toLowerCase();
    
    if (period === 'pm' && hours !== 12) {
      hours += 12;
    } else if (period === 'am' && hours === 12) {
      hours = 0;
    }
    
    return hours;
  }

  // Add a custom reminder (for manual creation)
  addCustomReminder(customReminderData) {
    try {
      const remindersCreated = [];
      const mainReminderId = customReminderData.id || `custom_${Date.now()}`;
      const releaseId = customReminderData.releaseId || mainReminderId; // Use provided releaseId or fallback
      const releaseName = customReminderData.releaseName || customReminderData.title || 'Custom Reminder';
      const releaseDate = customReminderData.releaseDate || customReminderData.due_date || new Date().toISOString();
      
      console.log('🔍 addCustomReminder - Input data:', {
        id: mainReminderId,
        releaseId,
        releaseName,
        releaseDate,
        originalData: customReminderData
      });
      
      // Validate required fields before creating
      if (!releaseId || !releaseName || !releaseDate) {
        console.error('❌ Missing required fields for custom reminder:', {
          releaseId: !!releaseId,
          releaseName: !!releaseName,
          releaseDate: !!releaseDate
        });
        throw new Error('Missing required fields for custom reminder');
      }
      
      // Create the main reminder
      const mainReminder = new ReminderInstance(
        'CUSTOM', // templateId
        releaseId, // releaseId - use the provided one for workflow consistency
        releaseName, // releaseName
        releaseDate, // releaseDate
        customReminderData.metadata || customReminderData // metadata
      );

      // Override some properties for custom reminders
      mainReminder.id = mainReminderId;
      mainReminder.status = 'pending';
      mainReminder.viewed = false;
      mainReminder.dueDate = customReminderData.due_date;
      
      // Ensure the required fields are properly set (double-check)
      mainReminder.releaseId = releaseId;
      mainReminder.releaseName = releaseName;
      mainReminder.releaseDate = releaseDate;

      // Create a custom template for this reminder
      mainReminder.template = {
        title: customReminderData.title,
        description: customReminderData.description,
        priority: customReminderData.priority || 'medium',
        actions: ['Complete', 'Remind Me Later']
      };

      this.reminders.push(mainReminder);
      remindersCreated.push(mainReminder);
      
      console.log('✅ Main custom reminder added:', mainReminder.id);
      console.log('🔍 Custom reminder data:', {
        id: mainReminder.id,
        releaseId: mainReminder.releaseId,
        releaseName: mainReminder.releaseName,
        releaseDate: mainReminder.releaseDate,
        metadata: mainReminder.metadata
      });
      
      // Process follow-up reminders if they exist
      const followUpReminders = customReminderData.follow_up_reminders || 
                               customReminderData.metadata?.follow_up_reminders || 
                               [];
      
      if (followUpReminders.length > 0) {
        console.log(`📅 Processing ${followUpReminders.length} follow-up reminders...`);
        const mainDueDate = new Date(customReminderData.due_date);
        const now = new Date();
        
        followUpReminders.forEach((followUp, index) => {
          const { days, time } = followUp;
          
          // Calculate follow-up due date (before the main due date)
          const followUpDueDate = new Date(mainDueDate);
          
          // Handle "day_of" or numeric days
          if (days !== 'day_of' && days !== '0') {
            const daysNum = parseInt(days);
            if (!isNaN(daysNum)) {
              followUpDueDate.setDate(followUpDueDate.getDate() - daysNum);
            }
          }
          
          // Set the time (e.g., "8am" -> 8:00)
          const hours = this.parseTimeToHours(time);
          followUpDueDate.setHours(hours, 0, 0, 0);
          
          // Only create if follow-up is in the future
          if (followUpDueDate > now) {
            const followUpReminder = new ReminderInstance(
              `FOLLOW_UP_${index + 1}`,
              `${mainReminderId}_followup_${index + 1}`,
              `Follow-up ${index + 1}: ${customReminderData.title}`,
              followUpDueDate.toISOString(),
              {
                ...customReminderData,
                isFollowUp: true,
                followUpNumber: index + 1,
                followUpDays: days,
                followUpTime: time,
                parentReminderId: mainReminderId
              }
            );
            
            followUpReminder.id = `${mainReminderId}_followup_${index + 1}`;
            followUpReminder.status = 'pending';
            followUpReminder.viewed = false;
            followUpReminder.dueDate = followUpDueDate.toISOString();
            
            followUpReminder.template = {
              title: `Follow-up ${index + 1}: ${customReminderData.title}`,
              description: `Follow-up reminder ${days === 'day_of' ? 'on the day of' : `${days} days before`} - ${customReminderData.description || customReminderData.title}`,
              priority: customReminderData.priority || 'medium',
              actions: ['Complete', 'Remind Me Later']
            };
            
            this.reminders.push(followUpReminder);
            remindersCreated.push(followUpReminder);
            
            console.log(`✅ Follow-up ${index + 1} added: ${followUpDueDate.toISOString()}`);
          } else {
            console.log(`⏭️  Skipped follow-up ${index + 1}: Date is in the past`);
          }
        });
      }
      
      this.saveReminders();
      
      console.log(`🎉 Created ${remindersCreated.length} total reminder(s)`);
      return remindersCreated.length > 1 ? remindersCreated : mainReminder;
    } catch (error) {
      console.error('Error adding custom reminder:', error);
      return null;
    }
  }

  // Complete a reminder
  completeReminder(reminderId) {
    const reminder = this.reminders.find(r => r.id === reminderId);
    if (reminder) {
      reminder.complete();
      this.saveReminders();
      return true;
    }
    return false;
  }

  // Revert a completed reminder back to pending
  revertReminder(reminderId) {
    const reminder = this.reminders.find(r => r.id === reminderId);
    if (reminder && reminder.status === 'completed') {
      reminder.revert();
      this.saveReminders();
      return true;
    }
    return false;
  }

  // Delete a reminder permanently
  deleteReminder(reminderId) {
    const initialLength = this.reminders.length;
    this.reminders = this.reminders.filter(r => r.id !== reminderId);
    
    if (this.reminders.length < initialLength) {
      this.saveReminders();
      return true;
    }
    return false;
  }

  // Snooze a reminder
  snoozeReminder(reminderId, hours = 24) {
    const reminder = this.reminders.find(r => r.id === reminderId);
    if (reminder) {
      reminder.snooze(hours);
      this.saveReminders();
      return true;
    }
    return false;
  }

  // Mark reminder as viewed
  markReminderViewed(reminderId) {
    const reminder = this.reminders.find(r => r.id === reminderId);
    if (reminder) {
      reminder.markViewed();
      this.saveReminders();
      return true;
    }
    return false;
  }

  // Get reminders for a specific release
  getRemindersForRelease(releaseId) {
    return this.reminders.filter(reminder => reminder.releaseId === releaseId);
  }

  // Check if reminders exist for a release
  hasRemindersForRelease(releaseId) {
    return this.reminders.some(reminder => reminder.releaseId === releaseId);
  }

  // Clean up old completed reminders (older than 30 days)
  cleanupOldReminders() {
    const thirtyDaysAgo = new Date(Date.now() - (30 * 24 * 60 * 60 * 1000));
    const initialCount = this.reminders.length;
    
    this.reminders = this.reminders.filter(reminder => {
      if (reminder.status === 'completed' && reminder.completedAt) {
        return new Date(reminder.completedAt) > thirtyDaysAgo;
      }
      return true;
    });
    
    if (this.reminders.length !== initialCount) {
      this.saveReminders();
    }
    
    return initialCount - this.reminders.length;
  }

  // Setup event listeners for catalog changes and autonomous checking
  setupEventListeners() {
    // Listen for catalog changes to automatically create reminders
    window.addEventListener('storage', (event) => {
      if (event.key === 'catalog_updated' || event.key === 'catalog_data') {
        console.log('📊 Catalog updated - checking for new releases requiring reminders');
        this.checkForNewReleases();
      }
    });

    // Check for new releases periodically (every 5 minutes)
    setInterval(() => {
      this.checkForNewReleases();
    }, 5 * 60 * 1000);

    // Check for recurring reminders daily
    setInterval(() => {
      this.checkRecurringReminders();
    }, 24 * 60 * 60 * 1000); // Every 24 hours

    // Initial checks after short delays
    setTimeout(() => {
      this.checkForNewReleases();
    }, 2000);
    
    setTimeout(() => {
      this.checkRecurringReminders();
    }, 5000);
  }

  // Check and create recurring reminders for booking workflows
  checkRecurringReminders() {
    console.log('🔄 Checking for recurring booking reminders...');
    
    const now = new Date();
    let remindersCreated = 0;
    
    // Get all booking reminders that are recurring and completed
    const completedRecurringReminders = this.reminders.filter(r => 
      r.metadata?.type === 'booking' && 
      r.status === 'completed' &&
      r.template?.recurringWeekly
    );
    
    completedRecurringReminders.forEach(reminder => {
      const templateId = reminder.templateId;
      const bookingId = reminder.releaseId;
      
      // Check if there's already a pending reminder for this booking+template
      const hasPendingReminder = this.reminders.some(r => 
        r.releaseId === bookingId &&
        r.templateId === templateId &&
        r.status === 'pending'
      );
      
      if (!hasPendingReminder) {
        // Calculate next due date (1 week from completion)
        const completedDate = new Date(reminder.completedAt);
        const nextDueDate = new Date(completedDate.getTime() + (7 * 24 * 60 * 60 * 1000));
        
        // Only create if next due date is before the show date
        const showDate = new Date(reminder.releaseDate);
        if (nextDueDate < showDate) {
          console.log(`📅 Creating recurring reminder for ${templateId}`);
          
          // Create new reminder with updated due date
          const newReminder = new ReminderInstance(
            templateId,
            bookingId,
            reminder.releaseName,
            reminder.releaseDate,
            {
              ...reminder.metadata,
              recurring: true,
              previousReminderId: reminder.id
            }
          );
          
          // Override the due date to be 1 week from completion
          newReminder.dueDate = nextDueDate.toISOString();
          
          this.reminders.push(newReminder);
          remindersCreated++;
        }
      }
    });
    
    // Handle special recurring patterns for Ticket Counts (escalating pattern)
    const ticketCountReminders = this.reminders.filter(r =>
      r.templateId === 'booking_ticket_counts' &&
      r.status === 'completed' &&
      r.metadata?.type === 'booking'
    );
    
    ticketCountReminders.forEach(reminder => {
      const showDate = new Date(reminder.releaseDate);
      const daysUntilShow = Math.floor((showDate - now) / (1000 * 60 * 60 * 24));
      
      // Determine frequency based on time until show
      let daysBetweenReminders = 7; // Default weekly
      if (daysUntilShow <= 21 && daysUntilShow > 7) {
        daysBetweenReminders = 3.5; // 2x weekly
      } else if (daysUntilShow <= 7) {
        daysBetweenReminders = 2.33; // 3x weekly (roughly every 2.33 days)
      }
      
      const completedDate = new Date(reminder.completedAt);
      const nextDueDate = new Date(completedDate.getTime() + (daysBetweenReminders * 24 * 60 * 60 * 1000));
      
      // Check if we need a new ticket count reminder
      const hasPendingTicketReminder = this.reminders.some(r =>
        r.releaseId === reminder.releaseId &&
        r.templateId === 'booking_ticket_counts' &&
        r.status === 'pending'
      );
      
      if (!hasPendingTicketReminder && nextDueDate < showDate) {
        const newReminder = new ReminderInstance(
          'booking_ticket_counts',
          reminder.releaseId,
          reminder.releaseName,
          reminder.releaseDate,
          {
            ...reminder.metadata,
            recurring: true,
            escalatingPattern: true,
            frequency: daysBetweenReminders
          }
        );
        
        newReminder.dueDate = nextDueDate.toISOString();
        this.reminders.push(newReminder);
        remindersCreated++;
      }
    });
    
    // Handle Guest List multi-stage reminders (1 week, 48 hours, 24 hours)
    const guestListReminders = this.reminders.filter(r =>
      r.templateId === 'booking_guest_list' &&
      r.status === 'completed' &&
      r.metadata?.type === 'booking'
    );
    
    guestListReminders.forEach(reminder => {
      const showDate = new Date(reminder.releaseDate);
      const daysUntilShow = Math.floor((showDate - now) / (1000 * 60 * 60 * 24));
      
      // Create 48-hour reminder if we're between 3-2 days out
      if (daysUntilShow <= 3 && daysUntilShow > 1) {
        const has48HourReminder = this.reminders.some(r =>
          r.releaseId === reminder.releaseId &&
          r.templateId === 'booking_guest_list' &&
          r.metadata?.stage === '48_hours'
        );
        
        if (!has48HourReminder) {
          const reminderDate = new Date(showDate.getTime() - (2 * 24 * 60 * 60 * 1000));
          const newReminder = new ReminderInstance(
            'booking_guest_list',
            reminder.releaseId,
            reminder.releaseName,
            reminder.releaseDate,
            { ...reminder.metadata, stage: '48_hours' }
          );
          newReminder.dueDate = reminderDate.toISOString();
          this.reminders.push(newReminder);
          remindersCreated++;
        }
      }
      
      // Create 24-hour reminder if we're 1 day out
      if (daysUntilShow <= 1 && daysUntilShow > 0) {
        const has24HourReminder = this.reminders.some(r =>
          r.releaseId === reminder.releaseId &&
          r.templateId === 'booking_guest_list' &&
          r.metadata?.stage === '24_hours'
        );
        
        if (!has24HourReminder) {
          const reminderDate = new Date(showDate.getTime() - (1 * 24 * 60 * 60 * 1000));
          const newReminder = new ReminderInstance(
            'booking_guest_list',
            reminder.releaseId,
            reminder.releaseName,
            reminder.releaseDate,
            { ...reminder.metadata, stage: '24_hours' }
          );
          newReminder.dueDate = reminderDate.toISOString();
          this.reminders.push(newReminder);
          remindersCreated++;
        }
      }
    });
    
    if (remindersCreated > 0) {
      this.saveReminders();
      console.log(`✅ Created ${remindersCreated} recurring booking reminders`);
    } else {
      console.log('ℹ️ No recurring reminders needed at this time');
    }
    
    return remindersCreated;
  }

  // Check catalog for new releases and create reminders autonomously
  async checkForNewReleases() {
    try {
      // Get catalog from the API endpoint that the catalog app uses
      const token = localStorage.getItem('dizeeToken');
      if (!token) return;

      // Use the same API endpoint as the catalog app
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.yourdomain.com'}/user/getCatalog`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${JSON.parse(token)}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        let catalog = data.data && Array.isArray(data.data) ? data.data : 
                      data.data && data.data.data ? data.data.data : [];
        
        console.log('🎵 Checking catalog for releases needing reminders...', catalog.length, 'tracks found');
        
        // PERFORMANCE SAFEGUARD: Filter out old releases before processing
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        const sixWeeksFromNow = new Date(Date.now() + (42 * 24 * 60 * 60 * 1000));
        
        const originalCount = catalog.length;
        catalog = catalog.filter(release => {
          if (!release.release_date) return false;
          const releaseDate = new Date(release.release_date);
          // Only process releases from 6 months ago to 6 weeks in the future
          return releaseDate >= sixMonthsAgo && releaseDate <= sixWeeksFromNow;
        });
        
        if (catalog.length < originalCount) {
          console.log(`🧹 Filtered catalog: ${originalCount} → ${catalog.length} releases (excluded very old/far future releases)`);
        }
        
        // LIMIT: Process max 1000 releases to prevent performance issues
        if (catalog.length > 1000) {
          console.warn(`⚠️ Large catalog detected (${catalog.length}), limiting to 1000 most recent`);
          catalog = catalog
            .sort((a, b) => new Date(b.release_date) - new Date(a.release_date))
            .slice(0, 1000);
        }
        
        let processedCount = 0;
        let createdCount = 0;
        
        catalog.forEach(release => {
          if (release.release_date) {
            processedCount++;
            const releaseId = release.id || release._id;
            const songName = release.track_name || release.title || release.name || release.song_title || 'Unknown Song';
            
            if (!this.hasRemindersForRelease(releaseId)) {
              // CREATE NEW REMINDERS - Only create reminders for future releases
              const releaseDate = new Date(release.release_date);
              const now = new Date();
              
              // Create reminders for releases within the next 6 weeks (our workflow window)
              if (releaseDate > now && releaseDate <= sixWeeksFromNow) {
                console.log(`⭐ Creating autonomous reminders for: ${songName}`);
                const newReminders = this.createRemindersForRelease(
                  releaseId,
                  songName, // Use the resolved song name directly
                  release.release_date,
                  {
                    artist: release.artist_name || release.artist,
                    label: release.label,
                    distributor: release.distributor,
                    autonomous: true // Mark as autonomously created
                  }
                );
                
                if (newReminders.length > 0) {
                  createdCount++;
                }
                
                // Show the reminder flow modal for autonomous releases
                if (window.showReminderFlowModal && newReminders.length > 0) {
                  console.log(`🎉 Showing reminder flow modal for autonomous release: ${songName}`);
                  // Add a small delay to ensure the app is ready for the modal
                  setTimeout(() => {
                    window.showReminderFlowModal(songName, newReminders.length);
                  }, 500);
                }
              }
            } else {
              // UPDATE EXISTING REMINDERS - Check if release date has changed
              this.updateRemindersForRelease(releaseId, release);
            }
          }
        });
        
        console.log(`✅ Processed ${processedCount} releases, created reminders for ${createdCount} new releases`);
      } else {
        console.warn('Failed to fetch catalog for autonomous reminders');
        // Fallback to localStorage if API fails
        this.checkLocalStorageCatalog();
      }
    } catch (error) {
      console.error('Error checking for new releases:', error);
      // Fallback to localStorage if API fails
      this.checkLocalStorageCatalog();
    }
  }

  // Fallback method to check localStorage catalog
  checkLocalStorageCatalog() {
    try {
      let catalog = JSON.parse(localStorage.getItem('catalog_data') || '[]');
      
      // PERFORMANCE SAFEGUARD: Same filtering as main method
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const sixWeeksFromNow = new Date(Date.now() + (42 * 24 * 60 * 60 * 1000));
      
      // Pre-filter catalog to avoid processing very old releases
      const originalCount = catalog.length;
      catalog = catalog.filter(release => {
        if (!release.release_date) return false;
        const releaseDate = new Date(release.release_date);
        // Only process releases from 6 months ago to 6 weeks in the future
        return releaseDate >= sixMonthsAgo && releaseDate <= sixWeeksFromNow;
      });
      
      if (catalog.length < originalCount) {
        console.log(`🧹 [LocalStorage] Filtered catalog: ${originalCount} → ${catalog.length} releases`);
      }
      
      catalog.forEach(release => {
        if (release.release_date) {
          const releaseId = release.id;
          const songName = release.track_name || release.title || release.name || release.song_title || 'Unknown Song';
          
          if (!this.hasRemindersForRelease(releaseId)) {
            // CREATE NEW REMINDERS
            const releaseDate = new Date(release.release_date);
            const now = new Date();
            
            if (releaseDate > now && releaseDate <= sixWeeksFromNow) {
              const newReminders = this.createRemindersForRelease(
                releaseId,
                songName, // Use the resolved song name directly
                release.release_date,
                {
                  artist: release.artist_name || release.artist,
                  label: release.label,
                  distributor: release.distributor,
                  autonomous: true
                }
              );
              
              // Show the reminder flow modal for autonomous releases from localStorage
              if (window.showReminderFlowModal && newReminders.length > 0) {
                console.log(`🎉 Showing reminder flow modal for localStorage release: ${songName}`);
                // Add a small delay to ensure the app is ready for the modal
                setTimeout(() => {
                  window.showReminderFlowModal(songName, newReminders.length);
                }, 500);
              }
            }
          } else {
            // UPDATE EXISTING REMINDERS
            this.updateRemindersForRelease(releaseId, release);
          }
        }
      });
    } catch (error) {
      console.error('Error checking localStorage catalog:', error);
    }
  }

  // Generate AI context for reminders
  generateAIContext() {
    const active = this.getActiveReminders();
    const overdue = this.getOverdueReminders();
    const today = this.getTodaysReminders();
    const upcoming = this.getUpcomingReminders();

    return {
      total_active: active.length,
      overdue_count: overdue.length,
      due_today: today.length,
      upcoming_week: upcoming.length,
      unviewed_count: this.getUnviewedCount(),
      critical_count: this.getCriticalReminders().length,
      active_reminders: active.map(r => ({
        id: r.id,
        title: r.template?.title,
        description: r.template?.description,
        release_name: r.releaseName,
        due_date: r.dueDate,
        status: r.status,
        priority: r.template?.priority,
        days_until_due: r.getDaysUntilDue(),
        is_overdue: r.isOverdue(),
        category: r.template?.category
      }))
    };
  }

  // Bulk cleanup utility for very old reminders
  bulkDeleteOldReminders(monthsOld = 6) {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsOld);
    
    const initialCount = this.reminders.length;
    
    // Filter out reminders older than cutoff that are not completed
    this.reminders = this.reminders.filter(reminder => {
      const dueDate = new Date(reminder.dueDate);
      // Keep completed reminders or recent reminders
      if (reminder.status === 'completed' || dueDate >= cutoffDate) {
        return true;
      }
      console.log(`🗑️ Removing old reminder: ${reminder.releaseName} (due: ${dueDate.toLocaleDateString()})`);
      return false;
    });
    
    const deletedCount = initialCount - this.reminders.length;
    
    if (deletedCount > 0) {
      this.saveReminders();
      console.log(`✅ Bulk deleted ${deletedCount} old reminders (older than ${monthsOld} months)`);
    }
    
    return deletedCount;
  }
}

// Global instance
export const remindersManager = new RemindersManager();

// Export convenience functions
export const getActiveReminders = () => remindersManager.getActiveReminders();
export const getUnviewedRemindersCount = () => remindersManager.getUnviewedCount();
export const getActiveRemindersCount = () => remindersManager.getActiveReminders().length;
export const getCriticalReminders = () => remindersManager.getCriticalReminders();
export const getCompletedReminders = () => remindersManager.getCompletedReminders();
export const completeReminder = (id) => remindersManager.completeReminder(id);
export const revertReminder = (id) => remindersManager.revertReminder(id);
export const deleteReminder = (id) => remindersManager.deleteReminder(id);
export const snoozeReminder = (id, hours) => remindersManager.snoozeReminder(id, hours);
export const markReminderViewed = (id) => remindersManager.markReminderViewed(id);
export const cleanupProblematicReminders = () => remindersManager.cleanupProblematicReminders();
export const addCustomReminder = (customReminderData) => remindersManager.addCustomReminder(customReminderData);
export const createRemindersForRelease = (releaseId, releaseName, releaseDate, metadata) => 
  remindersManager.createRemindersForRelease(releaseId, releaseName, releaseDate, metadata);

// Booking reminder functions
export const createRemindersForBooking = (bookingId, bookingData, metadata) => 
  remindersManager.createRemindersForBooking(bookingId, bookingData, metadata);
export const updateRemindersForBooking = (bookingId, updatedBooking) => 
  remindersManager.updateRemindersForBooking(bookingId, updatedBooking);
export const deleteRemindersForBooking = (bookingId) => 
  remindersManager.deleteRemindersForBooking(bookingId);

// Bulk cleanup function
export const bulkDeleteOldReminders = (monthsOld = 6) => 
  remindersManager.bulkDeleteOldReminders(monthsOld);

// Initialize cleanup on load
setTimeout(() => {
  remindersManager.cleanupOldReminders();
}, 1000);

// Auto-cleanup: Run bulk cleanup once per day to prevent accumulation
const AUTO_CLEANUP_KEY = 'dizee_last_cleanup_date';
const runAutoCleanup = () => {
  try {
    const lastCleanup = localStorage.getItem(AUTO_CLEANUP_KEY);
    const today = new Date().toDateString();
    
    // Only run once per day
    if (lastCleanup !== today) {
      const oldReminders = remindersManager.getAllReminders();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      const oldCount = oldReminders.filter(reminder => {
        const dueDate = new Date(reminder.dueDate);
        return reminder.status !== 'completed' && dueDate < sixMonthsAgo;
      }).length;
      
      // Only run cleanup if there are more than 100 old reminders to avoid unnecessary operations
      if (oldCount > 100) {
        console.log(`🧹 Running automatic daily cleanup (${oldCount} old reminders found)...`);
        const deletedCount = remindersManager.bulkDeleteOldReminders(6);
        if (deletedCount > 0) {
          console.log(`✅ Auto-cleanup: Removed ${deletedCount} old reminders`);
        }
      }
      
      localStorage.setItem(AUTO_CLEANUP_KEY, today);
    }
  } catch (error) {
    console.error('Error during auto-cleanup:', error);
  }
};

// Run auto-cleanup after a short delay
setTimeout(runAutoCleanup, 5000); 

// Debug functions for testing
window.testReminderModal = () => {
  if (window.showReminderFlowModal) {
    console.log('🧪 Testing reminder flow modal...');
    window.showReminderFlowModal('Test Release', 9);
  } else {
    console.error('❌ showReminderFlowModal not available on window object');
  }
};

window.testAutonomousReminders = () => {
  console.log('🧪 Testing autonomous reminder creation...');
  remindersManager.checkForNewReleases();
};

window.debugReminderSystem = () => {
  console.log('🔍 Debugging reminder system...');
  console.log('- showReminderFlowModal available:', !!window.showReminderFlowModal);
  console.log('- Current reminders count:', remindersManager.getAllReminders().length);
  
  // Check reminder settings
  try {
    const settings = JSON.parse(localStorage.getItem('dizee_reminder_settings') || '{}');
    console.log('- Reminder settings:', settings);
    console.log('- Release workflow modals disabled:', !!settings.disableReleaseWorkflowModals);
  } catch (error) {
    console.log('- Reminder settings: Error reading settings');
  }
  
  console.log('- Checking for new releases...');
  remindersManager.checkForNewReleases();
};

// Test function to reset reminder settings
window.resetReminderSettings = () => {
  localStorage.removeItem('dizee_reminder_settings');
  console.log('✅ Reminder settings have been reset');
};

// Bulk cleanup function for old reminders (useful after CSV imports)
window.cleanupOldReminders = (monthsOld = 6) => {
  console.log(`🧹 Cleaning up reminders older than ${monthsOld} months...`);
  const deletedCount = remindersManager.bulkDeleteOldReminders(monthsOld);
  if (deletedCount > 0) {
    console.log(`✅ Successfully deleted ${deletedCount} old reminders`);
    console.log(`📊 Remaining reminders: ${remindersManager.getAllReminders().length}`);
  } else {
    console.log('ℹ️ No old reminders found to delete');
  }
  return deletedCount;
};

// Test function to simulate a release date change
window.testReleaseDateUpdate = (releaseId, newDate) => {
  console.log('🧪 Testing release date update...');
  
  // Find existing reminders for this release
  const existingReminders = remindersManager.getAllReminders().filter(r => r.releaseId === releaseId);
  if (existingReminders.length === 0) {
    console.log('❌ No reminders found for release ID:', releaseId);
    console.log('Available releases:');
    const allReleases = [...new Set(remindersManager.getAllReminders().map(r => r.releaseId))];
    allReleases.forEach(id => {
      const reminder = remindersManager.getAllReminders().find(r => r.releaseId === id);
      console.log(`   - ${id}: "${reminder.releaseName}"`);
    });
    return;
  }
  
  console.log(`📅 Simulating date change for release "${existingReminders[0].releaseName}"`);
  console.log(`   Old date: ${existingReminders[0].releaseDate}`);
  console.log(`   New date: ${newDate}`);
  
  // Create mock release object
  const mockRelease = {
    id: releaseId,
    release_date: newDate,
    track_name: existingReminders[0].releaseName,
    artist_name: existingReminders[0].metadata?.artist || 'Test Artist'
  };
  
  // Trigger the update
  const result = remindersManager.updateRemindersForRelease(releaseId, mockRelease);
  console.log('Update result:', result);
};

// Test function to check reminder statuses
window.checkReminderStatuses = () => {
  const allReminders = remindersManager.getAllReminders();
  console.log('📊 Current reminder statuses:');
  
  const statusCounts = {
    pending: 0,
    overdue: 0,
    completed: 0,
    snoozed: 0
  };
  
  allReminders.forEach(reminder => {
    const isOverdue = reminder.isOverdue();
    const isDueToday = reminder.isDueToday();
    const status = isOverdue ? 'overdue' : reminder.status;
    statusCounts[status] = (statusCounts[status] || 0) + 1;
    
    console.log(`   ${reminder.releaseName} - ${reminder.template?.title}:`);
    console.log(`      Status: ${reminder.status} | Overdue: ${isOverdue} | Due Today: ${isDueToday} | Viewed: ${reminder.viewed}`);
    console.log(`      Due Date: ${reminder.dueDate} | Due Date Type: ${typeof reminder.dueDate}`);
    console.log(`      Has Methods: isOverdue=${typeof reminder.isOverdue}, isDueToday=${typeof reminder.isDueToday}`);
    console.log(`      ---`);
  });
  
  console.log('📈 Summary:', statusCounts);
};

// Function to debug tab filtering
window.debugTabFiltering = (tabName = 'overdue') => {
  const allReminders = remindersManager.getAllReminders();
  console.log(`🔍 Debugging "${tabName}" tab filtering:`);
  console.log(`Total reminders: ${allReminders.length}`);
  
  const filteredReminders = allReminders.filter(reminder => {
    switch (tabName) {
      case 'overdue':
        const isOverdue = reminder.isOverdue && reminder.isOverdue();
        console.log(`   ${reminder.template?.title}: isOverdue=${isOverdue}, status=${reminder.status}, viewed=${reminder.viewed}`);
        return isOverdue;
      case 'today':
        const isDueToday = reminder.isDueToday && reminder.isDueToday();
        console.log(`   ${reminder.template?.title}: isDueToday=${isDueToday}, status=${reminder.status}`);
        return isDueToday;
      case 'completed':
        const isCompleted = reminder.status === 'completed';
        console.log(`   ${reminder.template?.title}: isCompleted=${isCompleted}, status=${reminder.status}`);
        return isCompleted;
      default:
        return true;
    }
  });
  
  console.log(`Filtered results: ${filteredReminders.length} reminders`);
  return filteredReminders;
};

// Function to force refresh the reminders app
window.refreshRemindersApp = () => {
  console.log('🔄 Forcing reminders app refresh...');
  
  // Trigger storage event to force app reload
  window.dispatchEvent(new CustomEvent('localStorageUpdate', {
    detail: {
      key: 'dizee_reminders',
      newValue: localStorage.getItem('dizee_reminders')
    }
  }));
  
  console.log('✅ Refresh triggered');
};

// Comprehensive test function to debug the entire flow
window.testReminderFlow = () => {
  console.log('🧪 COMPREHENSIVE REMINDER FLOW TEST');
  console.log('=====================================');
  
  // Step 1: Check current state
  console.log('📊 Step 1: Current State');
  window.checkReminderStatuses();
  
  // Step 2: Debug tab filtering
  console.log('\n🔍 Step 2: Tab Filtering Debug');
  window.debugTabFiltering('overdue');
  
  // Step 3: Test date update (use first available release)
  console.log('\n📅 Step 3: Testing Date Update');
  const allReminders = remindersManager.getAllReminders();
  if (allReminders.length > 0) {
    const firstRelease = allReminders[0];
    const todayDate = new Date().toISOString().split('T')[0];
    console.log(`Updating release "${firstRelease.releaseName}" to today: ${todayDate}`);
    window.testReleaseDateUpdate(firstRelease.releaseId, todayDate);
  } else {
    console.log('❌ No reminders found to test with');
  }
  
  // Step 4: Check state after update
  setTimeout(() => {
    console.log('\n📊 Step 4: State After Update');
    window.checkReminderStatuses();
    
    // Step 5: Test tab filtering again
    console.log('\n🔍 Step 5: Tab Filtering After Update');
    window.debugTabFiltering('overdue');
    
    // Step 6: Force app refresh
    console.log('\n🔄 Step 6: Forcing App Refresh');
    window.refreshRemindersApp();
    
  }, 1000);
};

// Test function to simulate catalog save triggering reminder updates
window.testCatalogSaveTrigger = () => {
  console.log('🧪 Testing catalog save trigger...');
  
  // Simulate what happens when catalog is saved
  console.log('📅 Simulating catalog save event...');
  localStorage.setItem('catalog_updated', Date.now().toString());
  
  window.dispatchEvent(new CustomEvent('localStorageUpdate', {
    detail: {
      key: 'catalog_updated',
      newValue: Date.now().toString()
    }
  }));
  
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
  }, 500);
  
  console.log('✅ Catalog save events triggered');
  console.log('   - localStorage updated');
  console.log('   - localStorageUpdate event dispatched');
  console.log('   - checkOverdueReminders event scheduled');
  
  // Check if reminder system responds
  setTimeout(() => {
      console.log('📊 Checking reminder system response...');
  window.checkReminderStatuses();
}, 1000);
};

// Enable/disable detailed filtering debug
window.enableFilteringDebug = () => {
  window.debugRemindersFiltering = true;
  console.log('🔍 Filtering debug enabled - will show detailed logs in RemindersApp');
};

window.disableFilteringDebug = () => {
  window.debugRemindersFiltering = false;
  console.log('🔍 Filtering debug disabled');
};

// Enable/disable overdue check debug
window.enableOverdueDebug = () => {
  window.debugOverdueCheck = true;
  console.log('🔍 Overdue check debug enabled - will show detailed isOverdue() logs');
};

window.disableOverdueDebug = () => {
  window.debugOverdueCheck = false;
  console.log('🔍 Overdue check debug disabled');
};

// Complete debugging workflow
window.debugCompleteFlow = () => {
  console.log('🚀 COMPLETE DEBUGGING WORKFLOW');
  console.log('===============================');
  
  // Step 1: Enable debugging
  window.enableFilteringDebug();
  window.enableOverdueDebug();
  
  // Step 2: Check current state
  console.log('\n📊 Step 1: Current Reminder State');
  window.checkReminderStatuses();
  
  // Step 3: Test catalog trigger
  console.log('\n📅 Step 2: Testing Catalog Update Trigger');
  window.testCatalogSaveTrigger();
  
  // Step 4: Wait and check filtering
  setTimeout(() => {
    console.log('\n🔍 Step 3: Debug Tab Filtering (check RemindersApp logs)');
    window.debugTabFiltering('overdue');
    
    // Step 5: Force app refresh
    setTimeout(() => {
      console.log('\n🔄 Step 4: Force App Refresh');
      window.refreshRemindersApp();
      
      console.log('\n✅ Complete debugging workflow finished!');
      console.log('Check the RemindersApp for filtering debug logs.');
    }, 1000);
  }, 2000);
};

// Focused test for overdue detection issue
window.testOverdueDetection = () => {
  console.log('🧪 TESTING OVERDUE DETECTION');
  console.log('============================');
  
  // Enable overdue debugging
  window.enableOverdueDebug();
  
  // Get all reminders
  const allReminders = remindersManager.getAllReminders();
  console.log(`📋 Testing ${allReminders.length} reminders for overdue detection...`);
  
  // Test each reminder's overdue status
  allReminders.forEach((reminder, index) => {
    const isOverdue = reminder.isOverdue();
    const dueDate = new Date(reminder.dueDate);
    const now = new Date();
    
    console.log(`\n${index + 1}. "${reminder.template?.title}"`);
    console.log(`   Due: ${reminder.dueDate} (${dueDate.toLocaleDateString()})`);
    console.log(`   Status: ${reminder.status}`);
    console.log(`   Is Overdue: ${isOverdue}`);
    console.log(`   Should be overdue: ${now > dueDate && reminder.status === 'pending'}`);
  });
  
  // Count overdue reminders
  const overdueCount = allReminders.filter(r => r.isOverdue()).length;
  console.log(`\n📊 Summary: ${overdueCount} out of ${allReminders.length} reminders are overdue`);
  
  // Disable debug after test
  setTimeout(() => {
    window.disableOverdueDebug();
  }, 1000);
};

// Test current date and date comparisons
window.testDateComparisons = () => {
  console.log('📅 DATE COMPARISON TEST');
  console.log('======================');
  
  const now = new Date();
  console.log('Current date/time:', now.toISOString());
  console.log('Current date only:', now.toISOString().split('T')[0]);
  console.log('Local date string:', now.toLocaleDateString());
  
  // Test some sample dates
  const testDates = [
    '2024-12-24',
    '2025-09-28T00:00:00.000Z',
    '2025-10-07T00:00:00.000Z',
    new Date().toISOString().split('T')[0] // Today
  ];
  
  console.log('\nDate comparison tests:');
  testDates.forEach(dateStr => {
    const testDate = new Date(dateStr);
    const isPast = now > testDate;
    console.log(`${dateStr} -> ${testDate.toLocaleDateString()} (${isPast ? 'PAST' : 'FUTURE'})`);
  });
  
  // Check actual reminder dates
  const allReminders = remindersManager.getAllReminders();
  console.log(`\nActual reminder due dates (first 5):`);
  allReminders.slice(0, 5).forEach(reminder => {
    const dueDate = new Date(reminder.dueDate);
    const isPast = now > dueDate;
    console.log(`${reminder.template?.title}: ${reminder.dueDate} (${isPast ? 'PAST' : 'FUTURE'})`);
  });
};

// Quick test to verify the overdue fix
window.testOverdueFix = () => {
  console.log('🧪 TESTING OVERDUE FIX');
  console.log('=====================');
  
  const allReminders = remindersManager.getAllReminders();
  
  // Count reminders by status
  const statusCounts = {
    pending: 0,
    overdue: 0,
    completed: 0,
    snoozed: 0
  };
  
  // Count actual overdue detection
  let actualOverdueCount = 0;
  
  allReminders.forEach(reminder => {
    statusCounts[reminder.status] = (statusCounts[reminder.status] || 0) + 1;
    
    if (reminder.isOverdue()) {
      actualOverdueCount++;
      console.log(`✅ OVERDUE: "${reminder.template?.title}" (status: ${reminder.status})`);
    }
  });
  
  console.log('\n📊 Status Counts:');
  console.log('   Pending:', statusCounts.pending);
  console.log('   Overdue:', statusCounts.overdue);
  console.log('   Completed:', statusCounts.completed);
  console.log('   Snoozed:', statusCounts.snoozed);
  
  console.log('\n🎯 Overdue Detection:');
  console.log(`   Reminders with "overdue" status: ${statusCounts.overdue}`);
  console.log(`   Reminders detected as overdue: ${actualOverdueCount}`);
  console.log(`   Fix working: ${actualOverdueCount > 0 ? '✅ YES' : '❌ NO'}`);
  
  return { statusCounts, actualOverdueCount };
};

// Delete all reminders for a specific release
window.deleteRemindersForRelease = (releaseId, releaseName = 'Unknown Release') => {
  console.log(`🗑️ Deleting reminders for release: ${releaseName} (ID: ${releaseId})`);
  
  const deletedReminders = remindersManager.deleteRemindersForRelease(releaseId);
  
  console.log(`✅ Deleted ${deletedReminders.length} reminders for "${releaseName}"`);
  return deletedReminders;
};

// Test completed reminder display
window.testCompletedReminderDisplay = () => {
  console.log('🧪 TESTING COMPLETED REMINDER DISPLAY');
  console.log('=====================================');
  
  const allReminders = remindersManager.getAllReminders();
  const completedReminders = allReminders.filter(r => r.status === 'completed');
  
  console.log(`📊 Found ${completedReminders.length} completed reminders:`);
  
  completedReminders.forEach((reminder, index) => {
    console.log(`${index + 1}. "${reminder.template?.title}" - Status: ${reminder.status}`);
    console.log(`   Should show: "Completed" (not overdue info)`);
  });
  
  if (completedReminders.length === 0) {
    console.log('💡 No completed reminders found. Complete a reminder first to test this feature.');
  }
  
  return completedReminders;
};

// Test reminder deletion for release
window.testReminderDeletion = (releaseId = null) => {
  console.log('🧪 TESTING REMINDER DELETION');
  console.log('=============================');
  
  const allReminders = remindersManager.getAllReminders();
  
  if (!releaseId && allReminders.length > 0) {
    releaseId = allReminders[0].releaseId;
    console.log(`Using first available release ID: ${releaseId}`);
  }
  
  if (!releaseId) {
    console.log('❌ No release ID provided and no reminders found');
    return;
  }
  
  const relatedReminders = allReminders.filter(r => r.releaseId === releaseId);
  console.log(`📋 Found ${relatedReminders.length} reminders for release ${releaseId}:`);
  
  relatedReminders.forEach((reminder, index) => {
    console.log(`   ${index + 1}. "${reminder.template?.title}"`);
  });
  
  if (relatedReminders.length > 0) {
    console.log(`\n🗑️ Test deletion (this will actually delete them):`);
    console.log(`   Run: window.deleteRemindersForRelease('${releaseId}', 'Test Release')`);
  }
  
  return { releaseId, reminderCount: relatedReminders.length };
};

// Test the reminder flow modal
window.testReminderModal = (releaseName = 'Test Release', reminderCount = 9) => {
  console.log('🧪 TESTING REMINDER FLOW MODAL');
  console.log('===============================');
  console.log(`Release: ${releaseName}`);
  console.log(`Reminders: ${reminderCount}`);
  
  if (window.showReminderFlowModal) {
    console.log('✅ showReminderFlowModal function found, triggering modal...');
    window.showReminderFlowModal(releaseName, reminderCount);
  } else {
    console.log('❌ showReminderFlowModal function not available');
    console.log('💡 Make sure the RemindersNotification component is rendered');
  }
};

// Check and reset reminder modal settings
window.checkReminderModalSettings = () => {
  console.log('🧪 CHECKING REMINDER MODAL SETTINGS');
  console.log('====================================');
  
  try {
    const settings = localStorage.getItem('dizee_reminder_settings');
    const parsed = settings ? JSON.parse(settings) : {};
    
    console.log('Current settings:', parsed);
    
    if (parsed.disableReleaseWorkflowModals) {
      console.log('⚠️ Release workflow modals are DISABLED');
      console.log('💡 Run window.enableReminderModals() to re-enable them');
    } else {
      console.log('✅ Release workflow modals are ENABLED');
    }
    
    return parsed;
  } catch (error) {
    console.error('Error reading settings:', error);
    return {};
  }
};

// Enable reminder modals
window.enableReminderModals = () => {
  try {
    const currentSettings = localStorage.getItem('dizee_reminder_settings');
    const settings = currentSettings ? JSON.parse(currentSettings) : {};
    
    delete settings.disableReleaseWorkflowModals;
    localStorage.setItem('dizee_reminder_settings', JSON.stringify(settings));
    
    console.log('✅ Reminder modals have been re-enabled!');
    return true;
  } catch (error) {
    console.error('Error enabling modals:', error);
    return false;
  }
};

// Test reminder notification modals
window.testReminderNotificationModal = () => {
  console.log('🧪 TESTING REMINDER NOTIFICATION MODALS');
  console.log('========================================');
  
  // Trigger a check for overdue/due reminders
  console.log('🔔 Triggering reminder notification check...');
  window.dispatchEvent(new CustomEvent('checkOverdueReminders'));
  
  // Also force a manual check if possible
  const allReminders = remindersManager.getAllReminders();
  const overdueReminders = allReminders.filter(r => {
    return r.status === 'pending' && !r.viewed && r.isOverdue && r.isOverdue();
  });
  
  const dueTodayReminders = allReminders.filter(r => {
    return r.status === 'pending' && !r.viewed && r.isDueToday && r.isDueToday();
  });
  
  console.log(`📊 Found ${overdueReminders.length} overdue reminders`);
  console.log(`📊 Found ${dueTodayReminders.length} due today reminders`);
  
  if (overdueReminders.length === 0 && dueTodayReminders.length === 0) {
    console.log('💡 No eligible reminders for notification modals.');
    console.log('💡 Try creating a reminder with a past due date or today\'s date.');
    console.log('💡 Or run: window.createTestOverdueReminder()');
  }
  
  return { overdueReminders, dueTodayReminders };
};

// Create a test overdue reminder
window.createTestOverdueReminder = () => {
  console.log('🧪 CREATING TEST OVERDUE REMINDER');
  console.log('==================================');
  
  const testReleaseId = `test_overdue_${Date.now()}`;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const newReminders = remindersManager.createRemindersForRelease(
    testReleaseId,
    'Test Overdue Release',
    yesterday.toISOString().split('T')[0], // Format as YYYY-MM-DD
    { test: true }
  );
  
  console.log(`✅ Created ${newReminders.length} test reminders with yesterday's due date`);
  console.log('🔔 Now run: window.testReminderNotificationModal()');
  
  return newReminders;
}; 

// Test modal styling consistency
window.testModalStyling = () => {
  console.log('🎨 TESTING MODAL STYLING CONSISTENCY');
  console.log('===================================');
  
  const modalFeatures = [
    '✅ Black background with #FF2300 red border',
    '✅ Backdrop blur effect (bg-opacity-60 backdrop-blur-sm)',
    '✅ Rounded corners (rounded-2xl)',
    '✅ Consistent header with title and close button',
    '✅ 18px bold white title text',
    '✅ Gray close button with hover effect',
    '✅ Content section with proper spacing (mb-8)',
    '✅ Action buttons with red primary and outlined secondary',
    '✅ Consistent button sizing (py-3 px-4)',
    '✅ Proper focus states and transitions',
    '✅ Loading states with spinners',
    '✅ Disabled states handling'
  ];
  
  console.log('🎨 Updated Modal Components:');
  console.log('• ReminderFlowModal (Congrats modal)');
  console.log('• ReminderNotificationModal (Individual reminder popups)');
  console.log('• Modal.jsx (General utility modal)');
  console.log('• DizeeModal (Marketplace and other modals)');
  console.log('• Delete confirmation modals in:');
  console.log('  - Catalog EditCatalog');
  console.log('  - Catalog ViewAll');
  console.log('  - Merch EditMerch');
  console.log('  - Merch ViewAll');
  console.log('  - Bookings EditBooking');
  console.log('  - Bookings ViewAll');
  console.log('  - Catalog BulkImportCatalog (batch save modal)');
  
  console.log('');
  console.log('🎨 Modal Design Features:');
  modalFeatures.forEach(feature => console.log(feature));
  
  console.log('');
  console.log('🧪 To test modals:');
  console.log('• Try deleting items from catalog, merch, or bookings');
  console.log('• Add new releases to see the congrats modal');
  console.log('• Create overdue reminders: window.createTestOverdueReminder()');
  console.log('• Test notification modals: window.testReminderNotificationModal()');
  console.log('• Use marketplace to see DizeeModal styling');
  
  return {
    updatedComponents: [
      'ReminderFlowModal',
      'ReminderNotificationModal', 
      'Modal.jsx',
      'DizeeModal',
      'Delete confirmation modals (7 locations)'
    ],
    stylingFeatures: modalFeatures.length
  };
};

// ========== COMPREHENSIVE TEST SUITE ==========
// Load and expose test functions for browser console testing

// Lazy load test suite
let testSuite = null;
const loadTests = async () => {
  if (!testSuite) {
    try {
      const tests = await import('./reminderTests.js');
      testSuite = tests.default;
    } catch (error) {
      console.error('Failed to load test suite:', error);
    }
  }
  return testSuite;
};

// Expose main test runner to window
window.runReminderTests = async () => {
  const tests = await loadTests();
  if (tests) {
    return tests.runAllTests();
  }
};

window.runQuickReminderTest = async () => {
  const tests = await loadTests();
  if (tests) {
    return tests.quickTest();
  }
};

// Individual test access
window.testBookingReminders = async () => {
  const tests = await loadTests();
  if (tests) {
    tests.testBookingReminderCreation();
    tests.testBookingReminderDates();
    tests.testBookingReminderUpdates();
    tests.testRecurringReminders();
  }
};

window.testCatalogReminders = async () => {
  const tests = await loadTests();
  if (tests) {
    tests.testCatalogReminderCreation();
    tests.testCatalogReminderDates();
  }
};

window.testReminderOverdue = async () => {
  const tests = await loadTests();
  if (tests) {
    tests.testOverdueReminders();
    tests.testReminderNotifications();
  }
};

// Add help function for testing
window.reminderTestHelp = () => {
  console.log('\n🧪 DIZEE REMINDER SYSTEM - TEST COMMANDS');
  console.log('=========================================\n');
  console.log('📋 COMPREHENSIVE TESTS:');
  console.log('  window.runReminderTests()         - Run all 60+ tests');
  console.log('  window.runQuickReminderTest()     - Quick sanity check\n');
  console.log('🎫 BOOKING REMINDER TESTS:');
  console.log('  window.testBookingReminders()     - Test booking creation, dates, updates, recurring\n');
  console.log('🎵 CATALOG REMINDER TESTS:');
  console.log('  window.testCatalogReminders()     - Test release reminders\n');
  console.log('⏰ OVERDUE & NOTIFICATION TESTS:');
  console.log('  window.testReminderOverdue()      - Test overdue detection and notifications\n');
  console.log('🔧 DEBUGGING:');
  console.log('  window.debugReminderSystem()      - Debug system state');
  console.log('  remindersManager.getAllReminders() - Get all reminders');
  console.log('  remindersManager.checkRecurringReminders() - Trigger recurring check\n');
  console.log('💡 TIP: Run window.runReminderTests() for a complete system check!\n');
};

// Auto-display help on load
console.log('✨ Reminder Test Suite loaded! Type window.reminderTestHelp() for commands.'); 